import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { openInAppBrowser, isMobile } from "@/lib/mobile";
import { Button } from "@/components/ui/button";
import { Check, Crown, Diamond } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const TIER_VISUALS = {
    vip_gold: { icon: Crown, accent: "text-amber-400", border: "border-amber-700/40" },
    vip_diamond: { icon: Diamond, accent: "gold-text", border: "border-primary/40" },
    vip_platinum: { icon: Crown, accent: "text-zinc-200", border: "border-white/20" },
};

export default function VIPPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [tiers, setTiers] = useState([]);
    const [loadingTier, setLoadingTier] = useState(null);

    useEffect(() => {
        api.get("/vip/tiers").then(({ data }) => setTiers(data));
    }, []);

    const handleSubscribe = async (tierId) => {
        if (!user) {
            navigate("/auth?mode=login");
            return;
        }
        setLoadingTier(tierId);
        try {
            const { data } = await api.post("/payments/checkout", {
                package_id: tierId,
                origin_url: window.location.origin,
            });
            if (isMobile()) {
                await openInAppBrowser(data.url);
            } else {
                window.location.href = data.url;
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            setLoadingTier(null);
        }
    };

    return (
        <div className="min-h-screen bg-background" data-testid="vip-page">
            <Header />

            <section className="relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage:
                            "url(https://images.unsplash.com/photo-1647104568641-bd427704384c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdvbGQlMjB0ZXh0dXJlJTIwZGFya3xlbnwwfHx8fDE3NzczODg4ODJ8MA&ixlib=rb-4.1.0&q=85)",
                        backgroundSize: "cover",
                    }}
                />
                <div className="absolute inset-0 bg-background/80" />
                <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
                    <div className="overline mb-4">SMART Privée</div>
                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
                        Three tiers of<br />
                        <span className="italic gold-grad">indulgence.</span>
                    </h1>
                    <p className="mt-8 text-base text-muted-foreground max-w-xl mx-auto leading-relaxed">
                        Membership unlocks weekly cleaning, unlimited express bookings, monthly glam, and a dedicated concierge. For the ones who want everything taken care of.
                    </p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
                <div className="grid md:grid-cols-3 gap-6" data-testid="vip-tiers-grid">
                    {tiers.map((tier) => {
                        const v = TIER_VISUALS[tier.id] || TIER_VISUALS.vip_diamond;
                        const Icon = v.icon;
                        const isCurrent = user?.vip_tier === tier.id;
                        const isFeatured = tier.id === "vip_diamond";
                        return (
                            <article
                                key={tier.id}
                                className={`relative flex flex-col p-8 lg:p-10 bg-card border ${v.border} ${
                                    isFeatured ? "lg:scale-105 lg:shadow-2xl shadow-primary/10" : ""
                                }`}
                                data-testid={`vip-tier-${tier.id}`}
                            >
                                {isFeatured && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.3em]">
                                        Most Popular
                                    </div>
                                )}
                                <Icon size={32} strokeWidth={1} className={`${v.accent} mb-8`} />
                                <h2 className="font-serif text-3xl tracking-tight">{tier.name}</h2>
                                <div className="mt-6 flex items-baseline gap-2">
                                    <div className="font-serif text-5xl">
                                        <span className="gold-text">$</span>
                                        {tier.price_usd}
                                    </div>
                                    <div className="overline">USD/mo</div>
                                </div>
                                <ul className="mt-10 space-y-4 flex-1">
                                    {tier.perks.map((p) => (
                                        <li key={p} className="flex items-start gap-3 text-sm">
                                            <Check size={14} strokeWidth={1.5} className="gold-text mt-1 flex-shrink-0" />
                                            <span className="text-muted-foreground">{p}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Button
                                    onClick={() => handleSubscribe(tier.id)}
                                    disabled={loadingTier === tier.id || isCurrent}
                                    className={`mt-10 rounded-sm uppercase tracking-[0.2em] text-xs h-12 ${
                                        isFeatured
                                            ? "bg-primary text-primary-foreground hover:bg-primary/90"
                                            : "bg-transparent border border-white/15 text-foreground hover:bg-white/5"
                                    }`}
                                    data-testid={`vip-subscribe-${tier.id}`}
                                >
                                    {isCurrent ? "Active Member" : loadingTier === tier.id ? "Processing..." : "Become a Member"}
                                </Button>
                            </article>
                        );
                    })}
                </div>
            </section>

            <Footer />
        </div>
    );
}
